import React, { useEffect, useState } from "react";
import {
  getPickUpServices,
  updatePickUpService,
} from "../services/pickUpService";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Calendar, ChevronLeft, ChevronRight, CheckCircle2, PlayCircle, Edit3, Package, Phone, User, MapPin } from "lucide-react";

/* ---------- Utils ---------- */
const today = new Date().toISOString().split("T")[0];

/* ---------- Status Constants ---------- */
const STATUS = {
  PENDING: "PENDING",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED",
};

const STATUS_LABEL = {
  PENDING: "Pending",
  IN_PROGRESS: "In Progress",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
};

const getStatusStyles = (status) => {
  switch(status) {
    case 'PENDING': return 'bg-amber-100/50 text-amber-700 border-amber-200';
    case 'IN_PROGRESS': return 'bg-blue-100/50 text-blue-700 border-blue-200';
    case 'COMPLETED': return 'bg-emerald-100/50 text-emerald-700 border-emerald-200';
    case 'CANCELLED': return 'bg-rose-100/50 text-rose-700 border-rose-200';
    default: return 'bg-gray-100 text-gray-600 border-gray-200';
  }
}

const PickUp = () => {
  const [pickups, setPickups] = useState([]);
  const [selected, setSelected] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editStatusPickup, setEditStatusPickup] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedDate, setSelectedDate] = useState(today);

  /* ---------- API ---------- */
  const handleGetPickups = async () => {
    try {
      const res = await getPickUpServices();
      if (res?.status === 200) {
        setPickups(res?.data);
      }
    } catch (error) {
      console.error("Error fetching pickups:", error);
    }
  };

  //-------Update pickups status-------//
  const handleUpdatePickupStatus = async (id, status) => {
    try {
      const res = await updatePickUpService(id, { status });
      if (res?.status === 200) {
        setEditStatusPickup(null);
        handleGetPickups();
      }
    } catch (error) {
      console.error("Error updating pickup status:", error);
      toast.error("Failed to update status");
    }
  };

  useEffect(() => {
    handleGetPickups();
  }, []);

  /* ---------- Date Navigation ---------- */
  const goToPrevDate = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() - 1);
    setSelectedDate(d.toISOString().split("T")[0]);
  };

  const goToNextDate = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + 1);
    setSelectedDate(d.toISOString().split("T")[0]);
  };

  /* ---------- Selection ---------- */
  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  /* ---------- Actions ---------- */
  const startPickups = async () => {
    try {
      await Promise.all(
        selected?.map((id) =>
          updatePickUpService(id, { status: STATUS.IN_PROGRESS }),
        ),
      );
      setSelected([]);
      setShowModal(false);
      handleGetPickups();
    } catch (error) {
      console.error("Error starting pickups:", error);
      toast.error("Failed to start pickups");
    }
  };

  const markReached = async (id) => {
    try {
      const res = await updatePickUpService(id, {
        status: STATUS.COMPLETED,
      });

      if (res?.status === 200) {
        handleGetPickups();
      }
    } catch (error) {
      console.error("Error completing pickup:", error);
      toast.error("Failed to complete pickup");
    }
  };

  /* ---------- Filters ---------- */
  const filteredPickups = pickups?.filter((p) => {
    const matchesSearch =
      p._id?.toLowerCase().includes(search.toLowerCase()) ||
      p.customerName?.toLowerCase().includes(search.toLowerCase()) ||
      p.address?.toLowerCase().includes(search.toLowerCase());

    // Legacy pickups might not have a pickupDate property.
    // We retain search filtering and optionally check date if present or required.
    const hasDate = !!p.pickupDate;
    const matchesDate = hasDate ? (p.pickupDate.split('T')[0] === selectedDate) : true;

    // Remove strict Date constraint if not intentionally filtering by date only, 
    // or just return matchesSearch to mirror original behavior.
    return matchesSearch; 
  });

  return (
    <div className="p-4 md:p-8 bg-slate-50 min-h-full">
      {/* Premium Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8 bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-blue-100 p-2 rounded-lg text-blue-600">
              <Package size={24} />
            </div>
            <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Pickup Operations</h1>
          </div>
          <p className="text-sm text-slate-500 font-medium ml-12">
            Manage, dispatch, and track active pickup requests in real-time.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto">
          {/* Calendar Picker UI */}
          <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-xl px-2 py-1.5 shadow-inner w-full sm:w-auto">
            <button onClick={goToPrevDate} className="p-1.5 hover:bg-white rounded-md text-slate-400 hover:text-blue-500 transition-colors shadow-sm">
              <ChevronLeft size={18} />
            </button>
            <div className="flex items-center gap-2 px-3 text-slate-600 font-medium text-sm">
              <Calendar size={16} className="text-blue-500" />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="bg-transparent outline-none cursor-pointer"
              />
            </div>
            <button onClick={goToNextDate} className="p-1.5 hover:bg-white rounded-md text-slate-400 hover:text-blue-500 transition-colors shadow-sm">
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Search UI */}
          <div className="relative group w-full sm:w-72">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
            </div>
            <input
              type="text"
              placeholder="Search ID, customer, address..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-4 focus:ring-blue-50 focus:border-blue-500 transition-all font-medium text-slate-700 shadow-sm"
            />
          </div>
        </div>
      </div>

      {/* Floating Data Grid */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/80 border-b border-slate-200">
                <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase tracking-widest">Customer</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase tracking-widest">Phone</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase tracking-widest">Address</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase tracking-widest text-center">Package</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase tracking-widest text-center">Schedule</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase tracking-widest text-center">Status</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase tracking-widest text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <AnimatePresence>
                {filteredPickups?.map((pickup) => (
                  <motion.tr 
                    key={pickup._id} 
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    whileHover={{ backgroundColor: '#f8fafc' }}
                    className="transition-colors group"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                           <User size={14} />
                        </div>
                        <span className="text-[13px] font-semibold text-slate-900">{pickup?.customerName || "N/A"}</span>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 text-[13px] font-medium text-slate-600">
                      <div className="flex items-center gap-2">
                        <Phone size={14} className="text-slate-400" />
                        {pickup?.customerNumber || "N/A"}
                      </div>
                    </td>
                    
                    <td className="px-4 py-4 min-w-[200px]">
                      <div className="flex items-start gap-2">
                        <MapPin size={14} className="text-slate-400 mt-0.5 shrink-0" />
                        <span className="text-[13px] font-medium text-slate-600 leading-relaxed">
                          {pickup?.address || "N/A"}
                        </span>
                      </div>
                    </td>

                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-slate-100 text-slate-700 text-[11px] font-bold">
                        <Package size={12} /> {pickup?.parcelsNumber || 0}
                      </span>
                      {pickup?.parcelsWeight && (
                        <div className="text-[10px] text-slate-400 font-semibold mt-1 uppercase tracking-wider">{pickup.parcelsWeight} kg</div>
                      )}
                    </td>

                    <td className="px-6 py-4 text-center">
                      <div className="text-[13px] font-semibold text-slate-800">{pickup?.time || "Anytime"}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">{pickup?.pickupDate ? new Date(pickup.pickupDate).toLocaleDateString() : ""}</div>
                    </td>

                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider border ${getStatusStyles(pickup.status)}`}>
                        {pickup.status === STATUS.PENDING && <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mr-2 animate-pulse" />}
                        {STATUS_LABEL[pickup.status]}
                      </span>
                    </td>

                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => setEditStatusPickup(pickup)}
                          className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-transparent shadow-sm"
                          title="Override Status"
                        >
                          <Edit3 size={16} />
                        </button>

                        {pickup.status === STATUS.IN_PROGRESS && (
                          <button
                            onClick={() => markReached(pickup._id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-600 hover:bg-emerald-500 hover:text-white rounded-lg text-[11px] font-bold transition-all shadow-sm"
                          >
                            <CheckCircle2 size={14} /> Finish
                          </button>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </AnimatePresence>

              {filteredPickups?.length === 0 && (
                <tr>
                  <td colSpan="8" className="py-24 text-center">
                    <div className="flex flex-col items-center justify-center">
                       <Package className="w-12 h-12 text-slate-200 mb-3" />
                       <p className="text-lg font-bold text-slate-400">No pickups found</p>
                       <p className="text-sm text-slate-400 font-medium">Try adjusting your date or search parameters.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      <AnimatePresence>


        {editStatusPickup && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" />
            <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }} className="relative bg-white w-full max-w-sm rounded-[1.5rem] shadow-2xl overflow-hidden border border-slate-100">
               <div className="p-6 border-b border-slate-100">
                 <h2 className="text-lg font-bold text-slate-900">Override State</h2>
                 <p className="text-xs text-slate-400 font-medium mt-1 uppercase tracking-wider">Docket: {editStatusPickup._id}</p>
               </div>
               <div className="p-6 space-y-3 bg-slate-50">
                 {Object.values(STATUS)?.map((status) => (
                   <button
                     key={status}
                     onClick={() => handleUpdatePickupStatus(editStatusPickup._id, status)}
                     className="w-full py-3.5 rounded-xl text-sm font-bold border border-slate-200 bg-white transition-all shadow-sm hover:border-blue-300 hover:text-blue-700 hover:bg-blue-50 active:scale-95"
                   >
                     {STATUS_LABEL[status]}
                   </button>
                 ))}
               </div>
               <div className="p-4 border-t border-slate-100 bg-white flex justify-end">
                 <button onClick={() => setEditStatusPickup(null)} className="px-5 py-2 text-sm font-bold text-slate-500 hover:bg-slate-100 rounded-lg transition-colors">Discard</button>
               </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PickUp;
